import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Group, Loader, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { LoadMorePagination } from '@/components';
import { messageTemplatesService } from '@/services/messageTemplates.service';
import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useDeleteTemplateMutation,
  useUpdateTemplateStatusMutation,
} from '@/hooks';
import { useMessageTemplateTags, useMessageTemplates } from '@/hooks/message-templates';
import type { TemplateDTO, TemplateListItemDTO, TemplateSortParams } from '@/types/messageTemplates';
import { TagFilters } from './TagFilters/TagFilters';
import { CreateTemplateModal } from './CreateTemplateModal/CreateTemplateModal';
import { TemplatePreviewModal } from './TemplatePreviewModal/TemplatePreviewModal';
import { TemplateCard } from './TemplateCard/TemplateCard';
import styles from './MessageTemplates.module.sass';

type ModalState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; template: TemplateDTO; openDesigner: boolean }
  | { mode: 'loading'; templateId: string; openDesigner: boolean }
  | { mode: 'preview'; template: TemplateListItemDTO };

const SORT_OPTIONS: { value: string; label: string; params: TemplateSortParams }[] = [
  { value: 'title-asc', label: 'A-Z', params: { sortBy: 'title', sortOrder: 'asc' } },
  { value: 'title-desc', label: 'Z-A', params: { sortBy: 'title', sortOrder: 'desc' } },
  { value: 'created-desc', label: 'Recent Added', params: { sortBy: 'created_date', sortOrder: 'desc' } },
  { value: 'updated-desc', label: 'Recent Updated', params: { sortBy: 'updated_date', sortOrder: 'desc' } },
  { value: 'created-asc', label: 'Earliest Added', params: { sortBy: 'created_date', sortOrder: 'asc' } },
  { value: 'updated-asc', label: 'Earliest Updated', params: { sortBy: 'updated_date', sortOrder: 'asc' } },
];

export const MessageTemplates = () => {
  const [extraTemplates, setExtraTemplates] = useState<TemplateListItemDTO[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' });
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState<string>('created-desc');

  const sortParams = useMemo(() => SORT_OPTIONS.find((o) => o.value === sortValue)?.params, [sortValue]);

  const { data: tags = [], isLoading: isLoadingTags } = useMessageTemplateTags();
  const { data: templatesData, isLoading: isLoadingTemplates } = useMessageTemplates({ sort: sortParams });

  const createTagMutation = useCreateTagMutation();
  const deleteTagMutation = useDeleteTagMutation();
  const deleteTemplateMutation = useDeleteTemplateMutation();
  const updateStatusMutation = useUpdateTemplateStatusMutation();

  const firstPageTemplates = templatesData?.items ?? [];
  const total = templatesData?.total ?? 0;
  const templates = useMemo(() => [...firstPageTemplates, ...extraTemplates], [firstPageTemplates, extraTemplates]);
  const hasMore = templates.length < total;
  
  useEffect(() => {
    setExtraTemplates([]);
    if (firstPageTemplates.length > 0) {
      setCursor(firstPageTemplates[firstPageTemplates.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [templatesData]);

  const handleLoadMore = useCallback(async () => {
    if (templates.length >= total || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await messageTemplatesService.getTemplates({ sort: sortParams, cursor });
      if (data.items.length > 0) {
        setExtraTemplates((prev) => [...prev, ...data.items]);
        setCursor(data.items[data.items.length - 1].id);
      }
    } catch (error) {
      console.error('Failed to load more templates:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore, templates.length, total, sortParams]);

  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (selectedTagIds.length > 0) {
      result = result.filter((t) => t.tags.some((tag) => selectedTagIds.includes(tag.id)));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((t) => t.title.toLowerCase().includes(query));
    }
    return result;
  }, [templates, selectedTagIds, searchQuery]);

  const tagCountMap = useMemo(() => {
    const map = new Map<string, number>();
    templates.forEach((t) => t.tags.forEach((tag) => map.set(tag.id, (map.get(tag.id) ?? 0) + 1)));
    return map;
  }, [templates]);

  const handleTagClick = (tagId: string | null) => {
    if (!tagId) {
      setSelectedTagIds([]);
      return;
    }
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const handleAddTag = (name: string) => {
    createTagMutation.mutate({ name });
  };

  const handleDeleteTag = (tagId: string) => {
    deleteTagMutation.mutate(tagId, {
      onSuccess: () => {
        setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
      },
    });
  };

  const handleTemplateCreated = (_template: TemplateDTO) => {
    setModalState({ mode: 'closed' });
  };

  const handleTemplateUpdated = (_template: TemplateDTO) => {
    setModalState({ mode: 'closed' });
  };

  const handleToggleStatus = (template: TemplateListItemDTO) => {
    const newStatus = template.status === 'draft' ? 'published' : 'draft';
    setUpdatingStatusId(template.id);
    updateStatusMutation.mutate(
      {
        id: template.id,
        payload: {
          status: newStatus,
          title: template.title,
          content: template.content,
        },
      },
      {
        onSettled: () => {
          setUpdatingStatusId(null);
        },
      }
    );
  };

  const handleEditTemplate = async ({ templateId, openDesigner }: { templateId: string; openDesigner: boolean }) => {
    setModalState({ mode: 'loading', templateId, openDesigner });
    try {
      const fullTemplate = await messageTemplatesService.getTemplateById(templateId);
      setModalState({ mode: 'edit', template: fullTemplate, openDesigner });
    } catch (error) {
      console.error('Failed to fetch template for editing:', error);
      setModalState({ mode: 'closed' });
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    modals.openConfirmModal({
      title: 'Delete Template',
      children: <Text size="sm">Are you sure you want to delete this template? This action cannot be undone.</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteTemplateMutation.mutate(templateId);
      },
    });
  };

  const isTagMutating = createTagMutation.isPending || deleteTagMutation.isPending;
  const isLoading = isLoadingTags || isLoadingTemplates;

  return (
    <Stack mx="xxl" py="md" gap="md">
      <Group align="center" justify="space-between">
        <Group align="baseline" gap="xs">
          <Title order={2}>Templates</Title>
          <Text c="dimmed" size="xl">
            {total}
          </Text>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setModalState({ mode: 'create' })}>
          Create Template
        </Button>
      </Group>

      <CreateTemplateModal
        key={
          modalState.mode === 'edit' ? `${modalState.template.id}-${modalState.openDesigner ? 'designer' : 'form'}` : 'form'
        }
        opened={modalState.mode === 'create' || modalState.mode === 'edit'}
        startInDesigner={modalState.mode === 'edit' && modalState.openDesigner}
        onClose={() => setModalState({ mode: 'closed' })}
        onCreated={handleTemplateCreated}
        onUpdated={handleTemplateUpdated}
        availableTags={tags}
        editTemplate={modalState.mode === 'edit' ? modalState.template : null}
      />

      <TemplatePreviewModal
        opened={modalState.mode === 'preview'}
        onClose={() => setModalState({ mode: 'closed' })}
        title={modalState.mode === 'preview' ? modalState.template.title : ''}
        content={modalState.mode === 'preview' ? modalState.template.content : ''}
      />

      <TagFilters
        tags={tags}
        selectedTagIds={selectedTagIds}
        total={total}
        tagCountMap={tagCountMap}
        onTagClick={handleTagClick}
        onAddTag={handleAddTag}
        onDeleteTag={handleDeleteTag}
        disabled={isTagMutating}
      />

      <Group gap="sm" align="flex-end">
        <Select
          data={SORT_OPTIONS}
          value={sortValue}
          onChange={(value) => value && setSortValue(value)}
          allowDeselect={false}
          w={200}
        />
        <TextInput
          placeholder="Search templates by title..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
      </Group>

      <div className={styles.templatesGrid}>
        {isLoading && (
          <Stack align="center" justify="center" className={styles.loaderWrapper}>
            <Loader size="lg" />
          </Stack>
        )}

        {!isLoading && filteredTemplates.length === 0 && (
          <Stack align="center" justify="center" className={styles.emptyState}>
            <Text c="dimmed">No templates found</Text>
          </Stack>
        )}

        {!isLoading &&
          filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={(templateId) => handleEditTemplate({ templateId, openDesigner: false })}
              onEditInDesigner={(templateId) => handleEditTemplate({ templateId, openDesigner: true })}
              onDelete={handleDeleteTemplate}
              onPreview={(t) => setModalState({ mode: 'preview', template: t })}
              onToggleStatus={handleToggleStatus}
              isEditLoading={
                modalState.mode === 'loading' && !modalState.openDesigner && modalState.templateId === template.id
              }
              isDesignerLoading={
                modalState.mode === 'loading' && modalState.openDesigner && modalState.templateId === template.id
              }
              isDeleteLoading={deleteTemplateMutation.isPending}
              isStatusUpdating={updatingStatusId === template.id}
            />
          ))}
      </div>

      <LoadMorePagination
        loaded={templates.length}
        total={total}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />
    </Stack>
  );
};
